import {Stack} from '@commercial-helios/ui/src/components/stack'
import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Footer from '@/components/footer'
import Header from '@/components/header'
import {HubspotForm} from '@/components/hubspot-form'
import {bigformCompleted, bigformInteracted, bigformReached} from '@/components/lead-form/tracking'
import SEO from '@/components/seo'
import type {DemoPageQuery} from '@/graphql-types'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import {Scripts} from '@/utils/external-scripts'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import {Main} from '@helios/shared/components/skip-to-content'
import {useHubspotProperties} from '@helios/shared/hooks/use-hubspot-properties'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import {Layout, container, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {FormConfig} from '@helios/shared/types/consents'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import {
    LatestLeadSourceCaptureType,
    LifecycleStages,
} from '@helios/shared/types/marketing-campaigns'
import getEnvironmentField from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<DemoPageQuery>

const DemoPage = (props: Props) => {
    const data = props.data.prismicDemoPage.data
    const currentLocale = useLocale()
    const hubspotProperties = useHubspotProperties()
    const globalStrings = useGlobalStrings()
    const formConfig = getEnvironmentField({
        production: data.form_production,
        staging: data.form_staging,
    })
    const imperativeConsents = getEnvironmentField({
        production: data.imperative_consents_production,
        staging: data.imperative_consents_staging,
    })

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <BackgroundWrapper $color="muted">
            <HeaderWrapper>
                <Header hideLeftNav hideRightNav />
            </HeaderWrapper>

            <Main>
                <MainSection $variant="two-asymmetrical">
                    <Content $space={40}>
                        <Text variant="heading-base">{data.title}</Text>

                        <Stack as="ul" $space={20}>
                            {data.bullet_points.map((bullet, i) => (
                                <BulletPoint key={`${bullet.text}-${i}`}>
                                    <Icon image={bullet.icon} width={50} />
                                    <Text>{bullet.text}</Text>
                                </BulletPoint>
                            ))}
                        </Stack>
                    </Content>

                    <HubspotForm
                        content={{
                            title: data.form_title,
                            disclaimer: globalStrings.financial_partner_disclaimer,
                        }}
                        hubspotProperties={{
                            ...hubspotProperties,
                            lifecyclestage: LifecycleStages.Subscriber,
                            lead_source_latest_capture_type: LatestLeadSourceCaptureType.FreeDemo,
                        }}
                        formConfig={formConfig as FormConfig}
                        imperativeConsents={imperativeConsents}
                        onFormReached={() =>
                            bigformReached({
                                country: currentLocale.country,
                            })
                        }
                        onFormInteracted={(key) => bigformInteracted(key)}
                        onSubmitSuccess={(state) => bigformCompleted(state)}
                    />
                </MainSection>

                {data.social_proof_logos_section && (
                    <TitleWithIconGridSection
                        title={data.social_proof_logos_section.document.data.title}
                        items={data.social_proof_logos_section.document.data.logos.map((item) => ({
                            icon: item.logo,
                        }))}
                    />
                )}
            </Main>

            <Footer />
        </BackgroundWrapper>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<DemoPageQuery>) => {
    const pageData = data.prismicDemoPage.data

    return (
        <>
            <SEO
                location={location}
                pageContext={pageContext}
                title={pageData.meta_title}
                description={pageData.meta_description}
                openGraphImageUrl={pageData.open_graph_image?.url}
            />
            {/* Prefetching this script so I can execute the script immediatly on the /book-a-demo page*/}
            <link rel="prefetch" href={Scripts.HUBSPOT_MEETING_BOOKER}></link>
        </>
    )
}

const HeaderWrapper = styled.div`
    header {
        ${mq.min('xs')} {
            background: transparent;
        }
    }
`

const MainSection = styled(Layout)`
    display: grid;
    align-items: center;

    ${mq.min('xs')} {
        ${container}
    }

    ${mq.max('md')} {
        grid-template-columns: 1fr;
    }

    > &:first-child {
        ${mq.max('md')} {
            grid-row: 2;
        }
    }
`

const Content = styled(Stack)`
    padding-top: ${tokens.spacing40};
    padding-bottom: ${tokens.spacing40};

    ${mq.max('xs')} {
        ${container}
    }
`

const BulletPoint = styled.li`
    display: grid;
    column-gap: ${tokens.spacing20};
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
`

const Icon = styled(Image)`
    width: 50px;
`

export const query = graphql`
    query DemoPage($uid: String!, $locale: String!) {
        prismicDemoPage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title
                bullet_points {
                    icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    text
                }
                form_title
                form_production {
                    formId
                    consents {
                        type
                        communicationConsents {
                            id
                            required
                        }
                    }
                }
                form_staging {
                    formId
                    consents {
                        type
                        communicationConsents {
                            id
                            required
                        }
                    }
                }
                imperative_consents_production {
                    consent {
                        document {
                            ...ConsentWebsite
                        }
                    }
                }
                imperative_consents_staging {
                    consent {
                        document {
                            ...ConsentWebsite
                        }
                    }
                }
                social_proof_logos_section {
                    document {
                        ... on PrismicSocialProofLogosSection {
                            data {
                                title
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(DemoPage)
